<template>
  <AuthLayout :mode="mode">
    <Section heading="Settings" :mode="mode" />
    <div class="grid gap-6">
      <NumericCard :mode="mode" title="Company Settings" :number="1">
        <template #body>
          <ul class="grid list-disc gap-2 pl-4 sm:w-full">
            <li class="text-[18px] font-medium leading-none text-primary-50">
              <span
                class="text-[14px]"
                :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
                >Your company address</span
              >
            </li>
            <li class="text-[18px] font-medium leading-none text-primary-50">
              <span
                class="text-[14px]"
                :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
                >Top 10 manufacturers</span
              >
            </li>
            <li class="text-[18px] font-medium leading-none text-primary-50">
              <span
                class="text-[14px]"
                :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
                >Your top 7 specialities or logo</span
              >
            </li>
          </ul>
          <div class="sm:w-full">
            <div class="grid gap-2">
              <AppButton
                v-if="!canEditMember"
                :isLink="true"
                :to="{
                  name: 'member-details',
                  params: { id: loggedInUser.member.member_id },
                }"
                title="View Company Settings"
                type="secondary"
              />
              <AppButton
                v-else
                :isLink="true"
                :to="{
                  name: 'member-edit',
                  params: { id: loggedInUser.member.member_id },
                }"
                title="Edit Company Settings"
                type="secondary"
              />
            </div>
          </div>
        </template>
      </NumericCard>
      <NumericCard
        :mode="mode"
        title="Contact Settings"
        :number="2"
        :isBodyClasses="false"
      >
        <template #body>
          <div class="pt-5">
            <div
              class="mb-4 flex items-center justify-between sm:flex-col sm:gap-1"
            >
              <p
                class="text-[14px] font-medium"
                :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
              >
                Select an employee from the list below to edit or delete.
              </p>
              <RouterLink
                :to="{ name: 'add-contact' }"
                class="text-[14px] font-medium text-primary-100 hover:underline"
                >Add new contact</RouterLink
              >
            </div>
            <AppTable :mode="mode" :columns="columns">
              <template #rows>
                <tr v-for="(item, key) in contacts" :key="key">
                  <td>
                    <FallbackName
                      :to="{
                        name: 'contact-details',
                        params: {
                          id: item.contact_id,
                        },
                      }"
                      :first="item.first_name"
                      :last="item.last_name"
                    />
                  </td>
                  <td>
                    <FallbackText :value="item.email" :isTruncate="false" />
                  </td>
                  <td>
                    <RouterLink
                      v-if="
                        item.ok_to_update || isAdmin || contact.administrator
                      "
                      :to="{
                        name: 'contact-manufacturers',
                        params: { id: item.contact_id },
                      }"
                      >Edit mfrs</RouterLink
                    >
                    <span v-else>-</span>
                  </td>
                  <td>
                    <div class="flex items-center gap-2">
                      <TableAction
                        :isActive="
                          item.ok_to_update || isAdmin || contact.administrator
                        "
                        :mode="mode"
                        @itemAction="
                          $redirectTo(
                            item.contact_id === contact.contact_id
                              ? 'edit-contact'
                              : 'contact-edit',
                            item.contact_id,
                          )
                        "
                        action="edit"
                      />
                      <TableAction
                        :mode="mode"
                        :isActive="item.contact_id !== contact.contact_id"
                        @itemAction="confirmDelete(item.contact_id)"
                        action="delete"
                      />
                    </div>
                  </td>
                </tr>
              </template>
            </AppTable>
            <MobileTable
              :mode="mode"
              :fields="{
                contact: 'Contact',
                email: 'Email',
                manufacturer: 'Manufacturer',
                actions: 'Actions',
              }"
              :data="contacts"
            >
              <template #default="{ row, fieldKey }">
                <FallbackName
                  v-if="fieldKey === 'contact'"
                  :to="{
                    name: 'contact-details',
                    params: {
                      id: row.first_name,
                    },
                  }"
                  :first="row.first_name"
                  :last="row.last_name"
                  :isTruncate="false"
                />
                <template v-else-if="fieldKey === 'email'">
                  <FallbackText :value="row.email" :isTruncate="false" />
                </template>
                <template v-else-if="fieldKey === 'manufacturer'">
                  <RouterLink
                    v-if="row.ok_to_update || isAdmin || contact.administrator"
                    :to="{
                      name: 'contact-manufacturers',
                      params: { id: row.contact_id },
                    }"
                    class="text-[14px] font-medium"
                    :class="[
                      mode === 'dark' ? 'text-dark-text' : 'text-black-80',
                    ]"
                    >Edit mfrs</RouterLink
                  >
                  <span v-else>-</span>
                </template>
                <template v-else-if="fieldKey === 'actions'">
                  <div class="flex items-center gap-2">
                    <TableAction
                      :isActive="
                        row.ok_to_update || isAdmin || contact.administrator
                      "
                      :mode="mode"
                      @itemAction="
                        $redirectTo(
                          row.contact_id === contact.contact_id
                            ? 'edit-contact'
                            : 'contact-edit',
                          row.contact_id,
                        )
                      "
                      action="edit"
                    />
                    <TableAction
                      :mode="mode"
                      :isActive="row.contact_id !== contact.contact_id"
                      @itemAction="confirmDelete(row.contact_id)"
                      action="delete"
                    />
                  </div>
                </template>
              </template>
            </MobileTable>
            <Notes :list="notesList" :classes="'mt-5 sm:mt-4'" />
          </div>
        </template>
      </NumericCard>
    </div>
    <DeleteRecord
      :currentId="currentContactId"
      :mode="mode"
      @recordDeleted="recordDeleted"
      @cancelDelete="currentContactId = null"
    />
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import { useUserStore } from "@S/user";
import { useLoaderStore } from "@S/loader";
import { useToastStore } from "@S/toast";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import Notes from "@C/Common/Notes.vue";
import DeleteRecord from "@C/DeleteRecord.vue";
import NumericCard from "@C/Common/NumericCard.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AuthLayout from "@A/AuthLayout.vue";
import FallbackName from "@C/Tables/FallbackName.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import AppButton from "@C/Common/AppButton.vue";
import TableAction from "@C/Tables/TableAction.vue";

export default {
  name: "Settings",
  components: {
    AuthLayout,
    Section,
    DeleteRecord,
    Notes,
    NumericCard,
    AppTable,
    TableAction,
    FallbackName,
    FallbackText,
    MobileTable,
    AppButton,
  },
  setup() {
    const userStore = useUserStore();
    const loaderStore = useLoaderStore();
    const toastStore = useToastStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      loaderStore,
      toastStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      contacts: [],
      currentContactId: null,
      notesList: [
        "Click on edit mfrs to choose which manufacturers your company deals with AND which eTN Buy/Sell email you wish to receive from our trading network, if you are a subscriber.",
      ],
      columns: [
        {
          name: "Contact",
        },
        {
          name: "Email",
        },
        {
          name: "Manufacturer",
        },
        {
          name: "Actions",
          classes: "w-[50px]",
        },
      ],
    };
  },
  mounted() {
    this.getContacts();
  },
  methods: {
    async getContacts() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/us`);
        if (data) {
          this.contacts = data.contacts;
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    confirmDelete(id) {
      this.currentContactId = id;
    },
    recordDeleted() {
      this.getContacts();
      this.currentContactId = null;
      this.toastStore.show("Contact deleted successfully!", "success");
    },
  },
  computed: {
    loggedInUser() {
      return this.userStore.user;
    },
    contact() {
      return this.loggedInUser.contact;
    },
    isAdmin() {
      return this.loggedInUser.global_admin;
    },
    canEditMember() {
      // Global admins always; member administrators can edit their own
      // company. Mirrors OKToUpdate.CheckIt(ObjectToCheck, 'member').
      return this.isAdmin || this.contact?.administrator === true;
    },
  },
};
</script>
